/* -----------------Globals--------------- */
import { memo, useEffect } from "react";
import isEqual from "react-fast-compare";

/* -----------------Helpers & Hooks--------------- */
import { isWeb } from "@helpers/platform";
import QAChips from "@memorang/features/src/qa-search/QAChips";
import { useRouter } from "expo-router";
import { useAilaStore } from "../../hooks/useAilaStore";

/* -----------------Types--------------- */
import type { OriginalQuestionData } from "@memorang/features/src/qa-search/types/question";

import { useQASearch } from "@features/session/hooks/useQASearch";
import i18n from "@helpers/lib/i18n";
import { useAppStore } from "@hooks/useAppStore";
/* -----------------Constants--------------- */
import { EXCLUDED_EXAM_IDS } from "./constants";

type Props = {
	itemId: number;
	userPrompt: string;
	shouldRender?: boolean;
	originalQuestionData?: OriginalQuestionData;
	loading?: boolean;
	shouldShowAiDisclaimer?: boolean;
	handleShowAiDisclaimer?: () => void;
};

const QAChipsWrapper = ({
	itemId,
	userPrompt,
	shouldRender = true,
	originalQuestionData,
	loading,
	shouldShowAiDisclaimer,
	handleShowAiDisclaimer,
}: Props) => {
	const router = useRouter();
	const updateItemId = useAilaStore((state) => state.updateItemId);
	const updateSelectedQuery = useAilaStore(
		(state) => state.updateSelectedQuery,
	);
	const updateUserPrompt = useAilaStore((state) => state.updateUserPrompt);
	const updateIsItemAnswered = useAilaStore(
		(state) => state.updateIsItemAnswered,
	);
	const appId = useAppStore((store) => store.app.id);

	const showDrawer = useAilaStore((state) => state.showDrawer);
	const updateShowDrawer = useAilaStore((state) => state.updateShowDrawer);
	const updateOriginalQuestionData = useAilaStore(
		(state) => state.updateOriginalQuestionData,
	);

	// biome-ignore lint/correctness/useExhaustiveDependencies: // TODO fix me later
	useEffect(() => {
		updateItemId(`${itemId}`);
		updateUserPrompt(userPrompt);
	}, [itemId, userPrompt]);

	useEffect(() => {
		updateIsItemAnswered(shouldRender);
	}, [shouldRender, updateIsItemAnswered]);

	useEffect(() => {
		updateOriginalQuestionData(originalQuestionData);
	}, [originalQuestionData, updateOriginalQuestionData]);

	const { viewerId, examId, contextId } = useQASearch();

	if (EXCLUDED_EXAM_IDS.includes(examId) || !itemId || !userPrompt) {
		return null;
	}

	const handleChipPress = (query: string) => {
		if (shouldShowAiDisclaimer) {
			handleShowAiDisclaimer?.();
			return;
		}
		router.push({
			pathname: "/aila/[itemId]",
			params: {
				itemId,
				query,
			},
		});
	};

	return (
		<QAChips
			visible={shouldRender}
			userPrompt={userPrompt}
			vectorStoreParams={{
				examId,
				itemId: `${itemId}`,
				contextId,
			}}
			userId={viewerId!}
			handleChipPress={isWeb ? undefined : handleChipPress}
			showDialog={showDrawer}
			setShowDialog={(show) => {
				if (shouldShowAiDisclaimer) {
					handleShowAiDisclaimer?.();
					return;
				}
				updateShowDrawer(show);
			}}
			setSelectedQuery={updateSelectedQuery}
			showKeyboardShortcuts={false}
			loading={loading}
			outputLanguage={i18n.language}
			appId={appId}
		/>
	);
};

export default memo(QAChipsWrapper, isEqual);
